// app/src/dev/ContributeV2Panel.tsx
import React, { useMemo, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import { clearReceipts, loadReceipts } from "../simulations/receiptStore";
import { runContributeV2Send } from "../simulations/runContributeV2Send";

type Props = {
  defaultPoolV2?: string;
  defaultLamports?: string;

  // Optional (advanced) prefill only
  defaultMemberV2?: string;
  defaultVaultV2?: string;

  // UI policy toggles (optional)
  defaultAdvancedOpen?: boolean;
};

function shortPk(s?: string) {
  if (!s) return "—";
  return s.length > 12 ? `${s.slice(0, 4)}…${s.slice(-4)}` : s;
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function asTrimmed(v: unknown) {
  return String(v ?? "").trim();
}

function safePk(s: string): PublicKey | null {
  try {
    const t = asTrimmed(s);
    if (!t) return null;
    return new PublicKey(t);
  } catch {
    return null;
  }
}

export function ContributeV2Panel(props: Props) {
  // --- Inputs ---
  const [poolV2, setPoolV2] = useState(props.defaultPoolV2 || "");
  const [lamports, setLamports] = useState(props.defaultLamports || "1000");

  // Advanced overrides (optional)
  const [advancedOpen, setAdvancedOpen] = useState(!!props.defaultAdvancedOpen);
  const [memberOverride, setMemberOverride] = useState(props.defaultMemberV2 || "");
  const [vaultOverride, setVaultOverride] = useState(props.defaultVaultV2 || "");

  // --- State ---
  const [busy, setBusy] = useState<"idle" | "simulate" | "send">("idle");
  const [status, setStatus] = useState<string>("");
  const [sig, setSig] = useState<string>("");
  const [lastAction, setLastAction] = useState<string>("—");
  const [lastError, setLastError] = useState<string>("");
  const [refreshTick, setRefreshTick] = useState<number>(0);

  const phantom = (window as any)?.solana;

  const walletState = useMemo(() => {
    const detected = !!phantom;
    const isPhantom = !!phantom?.isPhantom;
    const connected = !!phantom?.publicKey;
    const pk = phantom?.publicKey ? phantom.publicKey.toString() : "";
    return { detected, isPhantom, connected, pk };
  }, [phantom?.publicKey]);

  const receipts = useMemo(() => {
    void refreshTick; // dependency
    return loadReceipts().filter((r) => r.kind === "contribute_v2");
  }, [refreshTick]);

  const poolPk = useMemo(() => safePk(poolV2), [poolV2]);
  const lamportsOk = useMemo(() => {
    const s = asTrimmed(lamports);
    if (!s) return false;
    // simple numeric guard (u64 in IDL)
    if (!/^\d+$/.test(s)) return false;
    try {
      // BigInt parse validates range-ish; u64 range is not enforced here
      BigInt(s);
      return true;
    } catch {
      return false;
    }
  }, [lamports]);

  const canRun = useMemo(() => {
    return !!poolPk && lamportsOk;
  }, [poolPk, lamportsOk]);

  async function ensureWalletConnected() {
    const w = (window as any).solana;
    if (!w) throw new Error("Phantom wallet not detected (window.solana missing).");
    if (typeof w.connect === "function") await w.connect();
    if (!w.publicKey) throw new Error("Wallet not connected.");
    return w;
  }

  async function onSimulate() {
    setSig("");
    setStatus("");
    setLastError("");
    setBusy("simulate");

    try {
      setLastAction("Simulate: connect wallet");
      await ensureWalletConnected();

      setLastAction("Simulate: import runner");
      // Stable import (no cache-busting). If you must refresh, hard reload the page.
      const mod = await import("../simulations/runContributeV2Simulation");

      setLastAction("Simulate: run");
      const res = await mod.runContributeV2Simulation({
        pool_v2: asTrimmed(poolV2),

        // Prefer Safe Mode: allow blanks here if you later update runContributeV2Simulation to derive PDAs.
        // For now, if advanced is open and fields provided, pass them through.
        member_v2: asTrimmed(memberOverride),
        vault_v2: asTrimmed(vaultOverride),

        lamports: asTrimmed(lamports),
      });

      setStatus("Simulation succeeded. Check DevTools Console for logs.");
      console.log("[ContributeV2Panel] Simulation result:", res);
      setLastAction("Simulate: done");
      setRefreshTick((x) => x + 1);
    } catch (e: any) {
      const msg = e?.message ? String(e.message) : String(e);
      console.error(e);
      setLastError(msg);
      setStatus(msg);
      setLastAction("Simulate: failed");
    } finally {
      setBusy("idle");
    }
  }

  async function onSend() {
    setSig("");
    setStatus("");
    setLastError("");
    setBusy("send");

    try {
      setLastAction("Send: connect wallet");
      const wallet = await ensureWalletConnected();

      setLastAction("Send: run");
      // Best practice for your stack: derive PDAs by default.
      // So we only pass overrides if advanced is open AND values are present.
      const res = await runContributeV2Send({
        pool_v2: asTrimmed(poolV2),
        lamports: asTrimmed(lamports),

        member_v2: advancedOpen ? asTrimmed(memberOverride) || undefined : undefined,
        vault_v2: advancedOpen ? asTrimmed(vaultOverride) || undefined : undefined,

        wallet,
      });

      const signature = res?.signature ? String(res.signature) : "";
      if (!signature) throw new Error("Send returned no signature.");

      setSig(signature);

      // Optional: display derived/effective PDAs if your updated runner returns them
      const effective = (res as any)?.effective;
      if (effective?.member_v2 && effective?.vault_v2) {
        setStatus(
          "SEND succeeded and was verified.\n" +
            `member_v2: ${effective.member_v2}\n` +
            `vault_v2: ${effective.vault_v2}\n` +
            "See receipts below."
        );
      } else {
        setStatus("SEND succeeded and was verified. See receipts below.");
      }

      setLastAction("Send: done");
      setRefreshTick((x) => x + 1);
    } catch (e: any) {
      const msg = e?.message ? String(e.message) : String(e);
      console.error(e);
      setLastError(msg);
      setStatus(msg);
      setLastAction("Send: failed");
      setRefreshTick((x) => x + 1);
    } finally {
      setBusy("idle");
    }
  }

  function onClearReceipts() {
    clearReceipts();
    setRefreshTick((x) => x + 1);
  }

  async function onCopySig() {
    if (!sig) return;
    const ok = await copyToClipboard(sig);
    setStatus(ok ? "Signature copied to clipboard." : "Could not copy signature (clipboard blocked).");
  }

  const advancedHint = useMemo(() => {
    return advancedOpen
      ? "Advanced is ON. Any provided PDAs will be strictly validated against derived PDAs."
      : "Safe Mode (recommended). Only pool_v2 + lamports are required; PDAs are derived automatically.";
  }, [advancedOpen]);

  return (
    <div style={{ border: "1px solid #333", borderRadius: 12, padding: 16, margin: "16px 0" }}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
        Contribute V2 — Simulate / Send (Option A: IDL-driven)
      </div>

      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 12 }}>
        <div>
          Wallet detected: <b>{walletState.detected ? "yes" : "no"}</b> | isPhantom:{" "}
          <b>{walletState.isPhantom ? "yes" : "no"}</b>
        </div>
        <div>
          Connected: <b>{walletState.connected ? "yes" : "no"}</b> | pubkey:{" "}
          <b>{shortPk(walletState.pk)}</b>
        </div>
        <div>
          Last action: <b>{lastAction}</b>
        </div>
        <div style={{ marginTop: 6 }}>
          Mode: <b>{advancedOpen ? "Advanced" : "Safe"}</b> —{" "}
          <span style={{ opacity: 0.85 }}>{advancedHint}</span>
        </div>
        {lastError ? (
          <div style={{ color: "#ff8a8a", marginTop: 6 }}>
            Last error: <b>{lastError}</b>
          </div>
        ) : null}
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <label>
          <div style={{ fontSize: 12, opacity: 0.8 }}>pool_v2 (required)</div>
          <input
            value={poolV2}
            onChange={(e) => setPoolV2(e.target.value)}
            placeholder="Pool PDA (V2)"
            style={{ width: "100%", padding: 10, borderRadius: 8 }}
          />
          {!poolPk && poolV2.trim() ? (
            <div style={{ fontSize: 12, color: "#ff8a8a", marginTop: 4 }}>
              Invalid public key format.
            </div>
          ) : null}
        </label>

        <label>
          <div style={{ fontSize: 12, opacity: 0.8 }}>lamports (required, u64)</div>
          <input
            value={lamports}
            onChange={(e) => setLamports(e.target.value)}
            placeholder="e.g. 1000"
            style={{ width: "100%", padding: 10, borderRadius: 8 }}
          />
          {!lamportsOk && lamports.trim() ? (
            <div style={{ fontSize: 12, color: "#ff8a8a", marginTop: 4 }}>
              Must be a whole-number string (u64).
            </div>
          ) : null}
        </label>

        {/* Advanced toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
          <input
            id="adv"
            type="checkbox"
            checked={advancedOpen}
            onChange={(e) => setAdvancedOpen(e.target.checked)}
          />
          <label htmlFor="adv" style={{ fontSize: 13 }}>
            Advanced (optional): provide member_v2 / vault_v2 overrides (strictly validated)
          </label>
        </div>

        {advancedOpen ? (
          <div style={{ display: "grid", gap: 10, padding: 12, borderRadius: 10, border: "1px solid #222" }}>
            <label>
              <div style={{ fontSize: 12, opacity: 0.8 }}>member_v2 (optional override)</div>
              <input
                value={memberOverride}
                onChange={(e) => setMemberOverride(e.target.value)}
                placeholder="Leave blank to confirm derived PDA"
                style={{ width: "100%", padding: 10, borderRadius: 8 }}
              />
            </label>

            <label>
              <div style={{ fontSize: 12, opacity: 0.8 }}>vault_v2 (optional override)</div>
              <input
                value={vaultOverride}
                onChange={(e) => setVaultOverride(e.target.value)}
                placeholder="Leave blank to confirm derived PDA"
                style={{ width: "100%", padding: 10, borderRadius: 8 }}
              />
            </label>

            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Recommendation: keep these blank unless you are troubleshooting PDA drift.
            </div>
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
          <button
            onClick={onSimulate}
            disabled={!canRun || busy !== "idle"}
            style={{ padding: "10px 14px", borderRadius: 10, cursor: "pointer" }}
          >
            {busy === "simulate" ? "Simulating..." : "Simulate"}
          </button>

          <button
            onClick={onSend}
            disabled={!canRun || busy !== "idle"}
            style={{ padding: "10px 14px", borderRadius: 10, cursor: "pointer" }}
          >
            {busy === "send" ? "Sending..." : "Send (Real Tx + Verify)"}
          </button>

          <button
            onClick={onClearReceipts}
            disabled={busy !== "idle"}
            style={{ padding: "10px 14px", borderRadius: 10, cursor: "pointer" }}
          >
            Clear Receipts
          </button>

          <button
            onClick={onCopySig}
            disabled={!sig || busy !== "idle"}
            style={{ padding: "10px 14px", borderRadius: 10, cursor: "pointer" }}
          >
            Copy Signature
          </button>
        </div>

        {status ? (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              borderRadius: 10,
              background: "#111",
              whiteSpace: "pre-wrap",
            }}
          >
            {status}
          </div>
        ) : null}

        {sig ? (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "#0b1a10" }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Last Signature</div>
            <div style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{sig}</div>
          </div>
        ) : null}

        <div style={{ marginTop: 16, fontSize: 14, fontWeight: 700 }}>Receipts (local)</div>
        <div style={{ display: "grid", gap: 8 }}>
          {receipts.length === 0 ? (
            <div style={{ fontSize: 12, opacity: 0.7 }}>No receipts yet.</div>
          ) : (
            receipts.slice(0, 10).map((r) => (
              <div
                key={(r.signature || r.createdAt) + r.lamports}
                style={{ border: "1px solid #222", borderRadius: 10, padding: 10 }}
              >
                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  <b>{new Date(r.createdAt).toLocaleString()}</b> | status:{" "}
                  <b>{r.confirmationStatus ?? "—"}</b> | fee: <b>{r.feeLamports ?? "—"}</b>
                </div>
                <div style={{ fontFamily: "monospace", wordBreak: "break-all", fontSize: 12 }}>
                  sig: {r.signature ? r.signature : "(none)"} <br />
                  lamports: {r.lamports}
                </div>
                {r.error ? (
                  <div style={{ color: "#ff8a8a", fontSize: 12, whiteSpace: "pre-wrap" }}>
                    error: {r.error}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
